
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '@/components/Layout';
import { Input } from '@/components/ui/input';
import Button from '@/components/Button';
import { Search as SearchIcon } from 'lucide-react';
import SolutionGrid from '@/components/SolutionGrid';
import { SolutionCardProps } from '@/components/SolutionCard';
import { SearchProviderFactory } from '@/services/searchProviders/SearchProviderFactory';
import DynamicMetaTags from '@/components/DynamicMetaTags';

// Function to fetch solutions using the configured search provider
const searchSolutions = async (query: string): Promise<SolutionCardProps[]> => {
  if (!query) return [];
  
  try {
    // Get the configured search provider
    const provider = await SearchProviderFactory.createProvider();
    
    console.log(`Using search provider: ${provider.getDisplayName()} (${provider.getProviderType()})`);
    console.log(`Expected response time: ${provider.getExpectedResponseTime()}ms`);
    console.log(`Natural language support: ${provider.isNaturalLanguageSupported()}`);
    
    // Perform search using the provider
    const results = await provider.search(query);
    
    return results;
    
  } catch (error) {
    console.error('Search error:', error);
    throw error;
  }
};

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryFromUrl = searchParams.get('q') || '';
  
  const [searchQuery, setSearchQuery] = useState(queryFromUrl);
  
  const { data: results = [], isLoading, error } = useQuery({
    queryKey: ['search', queryFromUrl],
    queryFn: () => searchSolutions(queryFromUrl),
    enabled: !!queryFromUrl,
    retry: 1, // Only retry once for search failures
    refetchOnWindowFocus: false, // Don't refetch when user focuses the window
    refetchOnReconnect: false, // Don't refetch when reconnecting to internet
    refetchInterval: false, // Never auto-refetch on an interval
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Update URL parameters using React Router
      setSearchParams({ q: searchQuery.trim() });
    }
  };

  return (
          <Layout className="pt-24">
        <DynamicMetaTags
          title={queryFromUrl ? `Search: ${queryFromUrl} - AIZen.tips` : "Search AI Solutions - AIZen.tips"}
          description={queryFromUrl ? `Search results for "${queryFromUrl}" in our AI solutions database.` : "Search through our comprehensive collection of AI solutions to find the perfect tool for your needs."}
          canonical={`https://aizen.tips/search${queryFromUrl ? `?q=${encodeURIComponent(queryFromUrl)}` : ''}`}
        />
      <div className="container px-4 py-8 md:py-12">
        <h1 className="text-3xl font-medium tracking-tight mb-8">Search Results</h1>
        
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-2 mb-8">
          <div className="relative flex-1">
            <Input
              type="text"
              placeholder="Search for AI solutions using natural language..."
              className="w-full pr-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button 
            type="submit" 
            className="w-full sm:w-auto"
            icon={<SearchIcon size={16} />}
            disabled={isLoading}
          >
            {isLoading ? 'Searching...' : 'Search'}
          </Button>
        </form>
        
        {queryFromUrl && !error && (
          <div className="mb-6">
            <p className="text-lg">
              Showing results for: <span className="font-medium">{queryFromUrl}</span>
            </p>
          </div>
        )}
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">
              {error instanceof Error ? error.message : 'Search failed due to an unknown error'}
            </p>
            <p className="text-red-600 text-sm mt-2">
              Please try again or use different search terms. If the issue persists, the AI search service may be temporarily unavailable.
            </p>
          </div>
        )}
        
        {!error && queryFromUrl && (
          <SolutionGrid 
            solutions={results} 
            isLoading={isLoading && !!queryFromUrl} 
          />
        )}
        
        {!queryFromUrl && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              Enter a search term to find AI solutions.
            </p>
          </div>
        )}
        
        {queryFromUrl && !isLoading && results.length === 0 && !error && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              No solutions found for "{queryFromUrl}". Try different search terms.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Search;
